/**
 * MarketplaceWorkflowDeployFooter — the v3 footer (workflow-name field +
 * deploy split button + warm-pool menu). Atomic-ish so both the in-
 * project detail modal (MarketplaceDetailModal in v3) and the public
 * wizard modal (MarketplaceDeployWizardModal after a project is picked)
 * render the SAME widget instead of two divergent copies.
 *
 * Owns the name input state, the existing-names fetch (used for instant
 * collision validation), and the split button + warm-pool dropdown.
 * Surfaces deploy via the onDeploy(opts) callback so the parent decides
 * what happens on success (toast, navigate, close modal).
 *
 * Required props:
 *   entry                  — { slug, ... } template; seeds the name input
 *   projectId              — for GET /projects/:id/workflows (existing
 *                            names for collision check)
 *   projectName            — used in the help text + deploy button label
 *   onDeploy({ workflowName, workflowNameIsDefault?, warm }) — async; parent
 *                            runs the deploy POST. `workflowNameIsDefault:
 *                            true` rides along ONLY when the box still holds
 *                            the value we prefilled — an untouched prefill is
 *                            a row id, not a name (see handleDeploy).
 *   deploying              — disable state from parent (true while POST
 *                            is in flight)
 *
 * The local validation runs synchronously against the cached list of
 * the project's workflow names. The server runs the same uniqueness
 * check on submit and 409s on race with a suggested alternative —
 * displayed by the parent's toast.
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { IonIcon } from '@ionic/react';
import {
  cloudUploadOutline,
  chevronDownOutline,
  flashOutline,
} from 'ionicons/icons';
import api from '../../services/api';
import Field from '../../components/atoms/Field/Field';
import { validateWorkflowName, suggestAvailableName } from '../../utils/workflow-name';
import { WARM_POOL_QUANTITY_OPTIONS } from '../../config/warmPool';


/**
 * The "Deploy as" box on the wire. Pure, exported for the request-builder
 * test: `{ workflowName }` when the user changed the prefill, plus
 * `workflowNameIsDefault: true` when they did not; `{}` for an empty box.
 */
export function deployNameFields(workflowName, seededName) {
  const name = typeof workflowName === 'string' ? workflowName.trim() : '';
  if (!name) return {};
  const fields = { workflowName: name };
  if (name === seededName) fields.workflowNameIsDefault = true;
  return fields;
}

const MarketplaceWorkflowDeployFooter = ({
  entry,
  projectId,
  projectName,
  onDeploy,
  deploying = false,
  // Hard external disable (e.g. the one-Copilot-per-account cap). When true
  // the Deploy split-button is greyed out regardless of name/agent validity;
  // the parent renders the explanatory text above the footer. `disabledReason`
  // doubles as the button tooltip so hover explains the grey-out too.
  // NOTE: missing REQUIRED integrations do NOT use this — that gate is
  // click-time in the parent modal (mark rows red + toast, never a disabled
  // button; product ruling 2026-07-07).
  disabled = false,
  disabledReason = '',
}) => {
  const [name, setName] = useState('');
  const [nameDirty, setNameDirty] = useState(false);
  // What WE put in the box (defaultSlug, auto-suffixed on collision) — the
  // prefill handleDeploy compares the final value against.
  const [seededName, setSeededName] = useState('');
  const [existingNames, setExistingNames] = useState([]);
  // DECLARED-EMPTY models (`models: []`) = this agent runs NO model at all — a
  // deterministic/service template (e.g. a sidecar host whose one node just
  // reports where the service lives). Worth stating, because it is a FACT
  // about the template, not a question being asked.
  //
  // Everything else that used to live here — the live /models list, the
  // requiredAgents filter, the project-default resolution, the latest-Claude
  // seed — is gone with the dropdown. Deploy asks nothing about the model.
  const noModel = Array.isArray(entry?.models) && entry.models.length === 0;

  // Fetch the project's existing workflow names so we can seed a non-
  // colliding default and give instant offline validation feedback.
  // Best-effort — if the request fails (or hasn't fired yet because the
  // project was just created) we fall back to server-only collision
  // checks and the wizard's 409 toast handler.
  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    api.get(`/projects/${projectId}/workflows`)
      .then((res) => {
        if (cancelled) return;
        const names = Array.isArray(res)
          ? res.map((w) => w.workflowType).filter(Boolean)
          : [];
        setExistingNames(names);
      })
      .catch(() => {
        if (!cancelled) setExistingNames([]);
      });
    return () => { cancelled = true; };
  }, [projectId]);

  // Seed the input from the template's declared `defaultSlug`, falling back to
  // the slug; auto-suffix on collision. Don't clobber a value the user has
  // already started typing.
  //
  // `defaultSlug` (marketplace-sync-core buildDesiredRow → the row → cardShape,
  // which does NOT omit it) is the template's declared SUGGESTED name, distinct
  // from `slug`, its IDENTITY (folder name, deploy route, sidecar name, the key
  // every already-deployed row uses). Reading it here is what makes a template
  // able to say "deploy me as `pingcode-mcp-service`" while staying `pingcode`.
  // Self-host already honoured it (`selfhosted/ui/index.html` — `it.defaultSlug
  // || slug`); cloud seeded from the slug alone, so the declaration was dead on
  // this surface. Same declaration, same result, both surfaces.
  const seedSlug = entry?.defaultSlug || entry?.slug;
  useEffect(() => {
    if (!seedSlug) return;
    if (nameDirty) return;
    const seeded = suggestAvailableName(seedSlug, existingNames) || seedSlug;
    setName(seeded);
    setSeededName(seeded);
  }, [seedSlug, existingNames, nameDirty]);

  const validation = useMemo(
    () => validateWorkflowName(name, existingNames),
    [name, existingNames]
  );

  const handleDeploy = useCallback((opts) => {
    if (deploying || !entry) return;
    if (disabled) return; // external hard-disable (e.g. one-Copilot cap)
    if (!validation.valid) return;
    // Project has no default AND user hasn't picked → flag the agent
    // select red. We STILL call onDeploy so the parent modal can flag
    // its own validation (missing integrations) on the SAME click —
    // the parent is the one that ultimately aborts when something's
    // wrong. Without this, only the first failing layer would flash
    // and the user would have to click Deploy twice to see everything
    // that's wrong.
    // No agent gate any more: deploy asks nothing about the model, so there is
    // nothing here that can be "not picked yet". `_agentMissing` is gone with
    // it — an unset model is now surfaced where it is FIXED, on the agent's
    // graph, by the Trigger check.
    // `workflowName` is ALWAYS the box — it is the ROW ID the user saw (the
    // template's defaultSlug, which for some templates differs from the slug
    // the server would fall back to), validated + collision-checked above.
    // Whether it is also the agent's NAME depends on one thing: did a human
    // change it from the prefill? An untouched prefill is not a name (founder
    // ruling, 2026-08-23) — it travels flagged `workflowNameIsDefault: true`
    // and the server stamps the template's declared display name instead of
    // the id (a plain install used to read `gbrain-kb` in the sidebar).
    // Anything else — a suggestion click, a typed value that happens to equal
    // the slug — is the chosen name and travels bare; there is no intent
    // detection beyond "changed from the prefill". Self-host sends the
    // identical pair (`deployNameFields` in selfhosted/ui/index.html);
    // __tests__/deploy-name-seed-parity pins both sides to this key.
    onDeploy?.({
      ...(opts || {}),
      ...deployNameFields(name, seededName),
    });
  }, [deploying, disabled, entry, onDeploy, name, seededName, validation]);

  // Warm-pool dropdown — toggled via the chevron next to the main CTA.
  const [warmMenuOpen, setWarmMenuOpen] = useState(false);
  const warmMenuRef = useRef(null);
  useEffect(() => {
    if (!warmMenuOpen) return undefined;
    const onDocClick = (e) => {
      if (warmMenuRef.current && !warmMenuRef.current.contains(e.target)) {
        setWarmMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setWarmMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [warmMenuOpen]);

  const deployLabel = projectName ? `Deploy to ${projectName}` : 'Deploy';

  return (
    <>
      <div className="mkt-modal-name-label-row">
        <label className="mkt-modal-name-label" htmlFor="mkt-deploy-name-input">
          Agent name
        </label>
        <p
          id="mkt-deploy-name-help"
          className={`mkt-modal-name-help ${validation.valid ? 'is-valid' : (name ? 'is-invalid' : '')}`}
          aria-live="polite"
        >
          {!name
            ? ''
            : validation.valid
              ? `Available — will deploy to ${projectName || 'this project'}`
              : (
                <>
                  {validation.reason || 'Pick a different name'}
                  {validation.suggestion && (
                    <>
                      {' — try '}
                      <button
                        type="button"
                        className="mkt-modal-name-suggestion"
                        onClick={() => { setName(validation.suggestion); setNameDirty(true); }}
                      >
                        {validation.suggestion}
                      </button>
                    </>
                  )}
                </>
              )}
        </p>
      </div>
      <div
        className={`mkt-modal-name-row ${validation.valid ? 'is-valid' : (name ? 'is-invalid' : '')}`}
        ref={warmMenuRef}
      >
        <Field
          id="mkt-deploy-name-input"
          type="text"
          className="mkt-modal-name-input"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setNameDirty(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && validation.valid && !deploying) {
              e.preventDefault();
              handleDeploy();
            }
          }}
          placeholder={seedSlug || 'workflow-name'}
          aria-invalid={!validation.valid && !!name}
          aria-describedby="mkt-deploy-name-help"
          autoComplete="off"
          spellCheck="false"
          disabled={deploying}
          status={name ? (validation.valid ? 'ok' : 'error') : null}
        />
        {/* The agent+model dropdown that used to live here is GONE. The model
            is chosen PER NODE, on the deployed agent's graph — one deploy-time
            pick could only name a single model for a graph whose nodes
            deliberately run different vendors (a cheap triage tier beside an
            expensive reviewer), so it named the wrong one for every node but
            one. A template that runs NO model still says so, since that is a
            fact about the template rather than a choice being asked for. */}
        {noModel && (
          <span className="mkt-field mkt-modal-no-model" title="This template's nodes are deterministic — no LLM runs.">
            No model
          </span>
        )}
        <div className="mkt-modal-deploy-split">
          <button
            type="button"
            className={`mkt-modal-deploy mkt-modal-deploy--main${deploying ? ' is-loading' : ''}`}
            onClick={() => handleDeploy()}
            disabled={deploying || disabled || !validation.valid}
            aria-label={deployLabel}
            aria-busy={deploying}
            title={disabled ? (disabledReason || undefined) : (!validation.valid ? 'Pick a valid name' : undefined)}
          >
            <IonIcon icon={cloudUploadOutline} aria-hidden="true" />
            Deploy
          </button>
          <button
            type="button"
            className="mkt-modal-deploy mkt-modal-deploy--chevron"
            onClick={() => setWarmMenuOpen((v) => !v)}
            disabled={deploying || disabled || !validation.valid}
            title={disabled ? (disabledReason || undefined) : undefined}
            aria-label="Deploy options"
            aria-haspopup="menu"
            aria-expanded={warmMenuOpen}
          >
            <IonIcon icon={chevronDownOutline} aria-hidden="true" />
          </button>
        </div>

        {warmMenuOpen && (
          <div className="mkt-modal-warm-menu" role="menu">
            <div className="mkt-modal-warm-menu-header">
              <IonIcon icon={flashOutline} aria-hidden="true" />
              <span>Warm pool</span>
            </div>
            <p className="mkt-modal-warm-menu-help">
              Skip ~60s cold start by keeping Fargate tasks pre-warmed.
              Paid feature — provisioning takes ~60s after deploy.
            </p>
            <ul className="mkt-modal-warm-menu-list">
              {WARM_POOL_QUANTITY_OPTIONS.map((n) => (
                <li key={n}>
                  <button
                    type="button"
                    role="menuitem"
                    className="mkt-modal-warm-menu-item"
                    onClick={() => {
                      setWarmMenuOpen(false);
                      handleDeploy({ warm: n });
                    }}
                    disabled={deploying || !validation.valid}
                  >
                    <span className="mkt-modal-warm-menu-label">
                      Deploy with <span className="mkt-modal-warm-menu-count">{n}</span>{' '}
                      warm worker{n === 1 ? '' : 's'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

export default MarketplaceWorkflowDeployFooter;
